import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../common/constants/private.ts';

type UseDeleteAgentProps = {
	onSuccess?: () => void;
	onError?: () => void;
	onInit?: (id: string) => void;
};

export const useDeleteAgent: (
	props: Omit<
		UseDeleteAgentProps & AssistanceServiceInjectedParams,
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => {
	deleteAgent: {
		data: void | null;
		error: ApiError | null;
		isSubmitting: boolean;
		mutate: (
			payload: string,
			callbacks?:
				| {
						onSuccess?: ((data: void) => void) | undefined;
						onError?: (error: ApiError) => void;
				  }
				| undefined,
		) => Promise<void | undefined>;
	};
} = createHookWithAssistanceService(
	({
		onSuccess,
		onError,
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		onInit,
	}: UseDeleteAgentProps & AssistanceServiceInjectedParams) => {
		const deleteAgent = useMutation({
			mutationFn: async (id: string) => {
				experienceTracker.start({
					id: ExperienceName.DELETE_AGENT,
					name: ExperienceName.DELETE_AGENT,
				});

				onInit?.(id);

				return await assistanceService.deleteAgent(id);
			},
			onSuccess: async (data) => {
				experienceTracker.succeed({
					name: ExperienceName.DELETE_AGENT,
				});

				onSuccess?.();

				return data;
			},
			onError: (error) => {
				experienceTracker.fail({
					name: ExperienceName.DELETE_AGENT,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});

				onError?.();
			},
		});

		return { deleteAgent };
	},
);
